<template>
  <div class="header-children"
       v-if="children.length">
    <div class="overflow-wrapper"
         :class="wrapperClasses"
         ref="scrollContainer"
         @scroll="setScrollState">
      <div class="child-container">
        <header-children-item class="child"
                              v-for="child in sortedChildren"
                              :key="child.id"
                              :class="childClasses"
                              :child="child"/>
      </div>
    </div>
    
    <transition name="fade"
                v-if="displayScrollButtons">
      <base-scroll-button class="button-left"
                          v-show="hasOverflow && !scrolledToLeftBound"
                          direction="left"
                          @mousedown.native="scrollLeft"
                          @mouseup.native="clearScrollLeftInterval"/>
    </transition>

    <transition name="fade"
                v-if="displayScrollButtons">
      <base-scroll-button class="button-right"
                          v-show="hasOverflow && !scrolledToRightBound"
                          direction="right"
                          @mousedown.native="scrollRight"
                          @mouseup.native="clearScrollRightInterval"/>
    </transition>
  </div>
</template>

<script>
  import HeaderChildrenItem from './HeaderChildrenItem.vue'

  export default {
    components: {
      HeaderChildrenItem
    },

    data() {
      return {
        hasOverflow: false,
        scrolledToLeftBound: false,
        scrolledToRightBound: false,
        scrollLeftInterval: null,
        scrollRightInterval: null
      }
    },

    computed: {
      ...mapGetters('children', {
        children: 'get'
      }),

      sortedChildren() {
        return _.sortBy(this.children, 'name')
      },

      displayScrollButtons() {
        return this.$mq !== 'mobile' && this.$mq !== 'small'
      },

      childClasses() {
        return {
          'child-mobile': this.$mq === 'mobile'
        }
      },

      wrapperClasses() {
        return {
          'overflow': this.hasOverflow,
          'overflow-mobile': this.$mq === 'mobile'
        }
      }
    },

    mounted() {
      window.addEventListener('resize', this.setLayout)
    },

    methods: {
      setLayout() {
        const container = this.$refs.scrollContainer

        if (container && container.scrollWidth) {
          this.hasOverflow = container.scrollWidth > container.clientWidth
        }

        this.setScrollState()
      },

      setScrollState() {
        const scrollContainer = this.$refs.scrollContainer

        this.scrolledToLeftBound = scrollContainer.scrollLeft === 0
        this.scrolledToRightBound = scrollContainer.offsetWidth + scrollContainer.scrollLeft >= scrollContainer.scrollWidth
      },

      scrollLeft() {
        this.scrollLeftInterval = setInterval(() => {
          this.$refs.scrollContainer.scrollLeft -= 10
        }, 10)
      },

      scrollRight() {
        this.scrollRightInterval = setInterval(() => {
          this.$refs.scrollContainer.scrollLeft += 10
        }, 10)
      },

      clearScrollLeftInterval() {
        clearInterval(this.scrollLeftInterval)
      },

      clearScrollRightInterval() {
        clearInterval(this.scrollRightInterval)
      }
    },

    watch: {
      children: {
        handler() {
          if (!this.children.length) {
            return
          }

          this.$nextTick(this.setLayout)
        },
        immediate: true
      },

      scrolledToLeftBound(isScrolled) {
        if (!isScrolled) {
          return
        }

        this.clearScrollLeftInterval()
      },

      scrolledToRightBound(isScrolled) {
        if (!isScrolled) {
          return
        }

        this.clearScrollRightInterval()
      }
    },

    beforeDestroy() {
      window.removeEventListener('resize', this.setLayout)
    }
  }
</script>

<style lang="scss" scoped>
  .header-children {
    position: relative;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
  }

  .overflow-wrapper {
    height: 100%;
    border-radius: 12px;
    overflow-x: auto;

    -ms-overflow-style: none; // Hide scrollbar on IE 10+
    scrollbar-width: none; // Hide scrollbar on Firefox

    &::-webkit-scrollbar {
      display: none; // Hide scrollbar on Safari and Chrome
    }

    &.overflow {
      height: calc(100% - 16px);
      padding: 0 22px;
      background-color: rgba(16, 7, 40, .2);
      box-shadow: 0 2px 4px 0 rgba(#000, .1);
    }

    &.overflow-mobile {
      padding: 0 8px
    }
  }

  .child-container {
    display: inline-flex;
    align-items: center;
    height: 100%;
  }

  .child {
    &:not(:last-child) {
      margin-right: 28px;
    }
  }

  .child-mobile {
    &:not(:last-child) {
      margin: 0;
    }
  }

  .button-left,
  .button-right {
    position: absolute;
    top: 50%;
  }

  .button-left {
    left: 0;
    transform: translateX(-50%) translateY(-50%);
  }

  .button-right {
    right: 0;
    transform: translateX(50%) translateY(-50%);
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity .1s ease-out;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
</style>
